import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner
} from 'reactstrap'
import branditRails from '../../api/brandit_rails'
import AdminNotesCard from './AdminNotesCard'

const isPendingOrder = order => order?.order_status === 'Pending'
const dateFormatter = date => moment(date).format('MMMM Do YYYY, h:mm a')
const digitalProofUrlFor = item => item?.digital_proofing_product?.url ?? null
const subOrderHasDigitalProofUrls = subOrder =>
  Boolean(subOrder?.sub_order_items?.some(item => Boolean(digitalProofUrlFor(item))))
const digitalProofLabelFor = item => {
  if (item?.product_name) return `View digital proof for ${item.product_name}`
  if (item?.product_sku) return `View digital proof for SKU ${item.product_sku}`

  return 'View digital proof'
}

const OrderDetailsModal = ({
  isOpen,
  toggle,
  size = 'xl',
  orderId,
  modalType,
  onApprove,
  onDeny,
  initialOrder,
  currentUser
}) => {
  const [orderData, setOrderData] = useState(initialOrder || null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [generatingInvoice, setGeneratingInvoice] = useState(false)
  const [notes, setNotes] = useState('')
  const hasApprovalNotes = notes.trim().length > 0
  const canReviewOrder = isPendingOrder(orderData)
  const approveDisabled = submitting || !canReviewOrder
  const denyDisabled = submitting || !canReviewOrder || !hasApprovalNotes

  useEffect(() => {
    if (isOpen && orderId) {
      const fetchData = async () => {
        try {
          const orderRes = await branditRails.get(`/admin_user/order_approvals/${orderId}`)
          let order = orderRes.data

          if (order.order && (order.order_items || order.customer || order.promotion_orders)) {
            order = {
              ...order.order,
              order_items: order.order_items || order.order.order_items || [],
              customer: order.customer || order.order.customer || {},
              promotion_orders: order.promotion_orders || order.order.promotion_orders || [],
              sub_orders: order.order.sub_orders || [],
              payments: order.order.payments || [],
              customer_address: order.order.customer_address || order.customer_address || null,
              billing_address: order.order.billing_address || order.billing_address || null
            }
          }

          if (order.customer_address) {
            order.customer_address = {
              ...order.customer_address,
              address_1: order.customer_address.address_1 || order.customer_address.address_line_1,
              address_2: order.customer_address.address_2 || order.customer_address.address_line_2,
              zip: order.customer_address.zip || order.customer_address.zip_code,
              state: order.customer_address.state || order.customer_address.region
            }
          }
          if (order.billing_address) {
            order.billing_address = {
              ...order.billing_address,
              address_1: order.billing_address.address_1 || order.billing_address.address_line_1,
              address_2: order.billing_address.address_2 || order.billing_address.address_line_2,
              zip: order.billing_address.zip || order.billing_address.zip_code,
              state: order.billing_address.state || order.billing_address.region
            }
          }

          setOrderData(order)
        } catch {
          toast.error('Error fetching order details')
        } finally {
          setLoading(false)
        }
      }
      fetchData()
    }
  }, [isOpen, orderId])

  const handleClose = () => {
    setNotes('')
    toggle()
  }

  const submitOrderApproval = async (order, notes, isApproved) => {
    if (!currentUser?.id || !order?.id) {
      toast.error(`Failed to ${isApproved ? 'approve' : 'deny'} order. Please try again.`)
      return
    }

    if (!isPendingOrder(order)) {
      toast.error('Only pending orders can be approved or denied.')
      return
    }

    if (!isApproved && !notes.trim()) {
      toast.error('Approval notes are required to deny this order.')
      return
    }

    setSubmitting(true)
    try {
      const formData = new FormData()
      const apiStatus = isApproved ? 'Approved' : 'Cancelled'
      formData.append('order_status', apiStatus)
      formData.append('approved_at', new Date().toString())
      formData.append('order_approval_notes', notes || '')

      await branditRails.patch(`/admin_user/order_approvals/${order.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      toast.success(`Order #${order.order_number} ${isApproved ? 'approved' : 'denied'} successfully`)
      if (onApprove && isApproved) onApprove(order, notes)
      if (onDeny && !isApproved) onDeny(order, notes)
      handleClose()
    } catch {
      toast.error(`Failed to ${isApproved ? 'approve' : 'deny'} order. Please try again.`)
    } finally {
      setSubmitting(false)
    }
  }

  const headerText = () => {
    if (!orderData) return ''
    if (modalType === 'view') return `Order Details - ${orderData.order_number}`
    if (modalType === 'approve') return `${orderData.order_number} - Approve Order?`
    if (modalType === 'deny') return `${orderData.order_number} - Deny Order?`
  }

  const generateOrderInvoice = async () => {
    if (!orderData?.id || generatingInvoice) return

    setGeneratingInvoice(true)
    try {
      const response = await branditRails.get(`/orders/${orderData.id}/generate_invoice`)
      if (response?.data?.url) {
        window.open(response.data.url, '_blank')
      } else {
        toast.error('Failed to generate order invoice. Please try again.')
      }
    } catch {
      toast.error('Failed to generate order invoice. Please try again.')
    } finally {
      setGeneratingInvoice(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size={size}>
      <ModalHeader toggle={handleClose}>{headerText()}</ModalHeader>
      <ModalBody>
        {loading || !orderData ? (
          <div className="text-center">
            <Spinner color="primary" />
            <p>Loading order details...</p>
          </div>
        ) : (
          <>
            {modalType === 'view' ? (
              <>
                <div className="d-flex justify-content-end mb-3">
                  <Button
                    color="info"
                    size="sm"
                    onClick={generateOrderInvoice}
                    disabled={generatingInvoice || orderData.order_status === 'Processing'}
                  >
                    {generatingInvoice ? 'Generating...' : 'Order Invoice'}
                  </Button>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <Card className="mb-3">
                      <CardHeader style={{ color: 'var(--mf-white)' }}>
                        <strong>Account Info</strong>
                      </CardHeader>
                      <CardBody>
                        <p>
                          <strong>Name:</strong> {orderData.customer?.full_name}
                        </p>
                        <p>
                          <strong>Email:</strong> {orderData.customer?.email}
                        </p>
                        {orderData.customer?.ecpid && (
                          <p>
                            <strong>ECPID:</strong> {orderData.customer.ecpid}
                          </p>
                        )}
                        {orderData.customer?.eclid && (
                          <p>
                            <strong>ECLID:</strong> {orderData.customer.eclid}
                          </p>
                        )}
                      </CardBody>
                    </Card>
                    <Card className="mb-3">
                      <CardHeader style={{ color: 'var(--mf-white)' }}>
                        <strong>General Info</strong>
                      </CardHeader>
                      <CardBody>
                        <p>
                          <strong>Order Number:</strong> {orderData.order_number}
                        </p>
                        <p>
                          <strong>Client:</strong> {orderData.client_name}
                        </p>
                        <p>
                          <strong>Portal:</strong> {orderData.portal_name}
                        </p>
                        <p>
                          <strong>Store View:</strong> {orderData.location_name}
                        </p>
                        <p>
                          <strong>Order Status:</strong> {orderData.order_status}
                        </p>
                        <p>
                          <strong>Ordered At:</strong> {dateFormatter(orderData.created_at)}
                        </p>
                      </CardBody>
                    </Card>
                    <Card className="mb-3">
                      <CardHeader style={{ color: 'var(--mf-white)' }}>
                        <strong>Checkout Information Form</strong>
                      </CardHeader>
                      <CardBody>
                        {orderData.checkout_form && orderData.checkout_form.length > 0 ? (
                          <div>
                            {orderData.checkout_form.map((item, index) => (
                              <div key={index}>
                                {item.options.some(option => option.checked) && (
                                  <h5 style={{ color: 'rgba(0, 0, 0, 0.87)' }}>{item.name}</h5>
                                )}
                                {item.field_type === 'checkbox' && (
                                  <ul className="checkout-form-bullets">
                                    {item.options
                                      .filter(option => option.checked)
                                      .map((option, optionIndex) => (
                                        <li key={optionIndex}>{option.value}</li>
                                      ))}
                                  </ul>
                                )}
                                {item.field_type === 'radio' && (
                                  <ul className="checkout-form-bullets">
                                    {item.options
                                      .filter(option => option.checked)
                                      .map((option, optionIndex) => (
                                        <li key={optionIndex}>{option.value}</li>
                                      ))}
                                  </ul>
                                )}
                                {item.field_type === 'customer_notes' &&
                                  item.options.map((option, optionIndex) => (
                                    <div key={optionIndex}>
                                      <strong>Customer Notes: </strong>
                                      {option.value}
                                    </div>
                                  ))}
                              </div>
                            ))}
                            {orderData.site_confirmation_wall_message && (
                              <>
                                <br />
                                <dt>Site Confirmation Message:</dt>
                                <dd>{orderData.site_confirmation_wall_message}</dd>
                              </>
                            )}
                          </div>
                        ) : (
                          <p>This order does not have a checkout information form.</p>
                        )}
                      </CardBody>
                    </Card>
                    <AdminNotesCard order={orderData} dateFormatter={dateFormatter} />
                  </div>
                  <div className="col-md-6">
                    <Card className="mb-3">
                      <CardHeader style={{ color: 'var(--mf-white)' }}>
                        <strong>Order Cost & Payment</strong>
                      </CardHeader>
                      <CardBody>
                        <p>
                          <strong>Payment Status:</strong>{' '}
                          {orderData.billing_status
                            ? orderData.billing_status
                                .split('_')
                                .map(w => w.toUpperCase())
                                .join(' ')
                            : 'PAID'}
                        </p>
                        {orderData.billing_status === 'net_po_ready' && (
                          <p>
                            <strong>PO Number:</strong> {orderData.po_number}
                          </p>
                        )}
                        <p>
                          <strong>Total Items:</strong>{' '}
                          {orderData.order_items?.reduce((total, item) => total + (item.quantity || 0), 0) || 0}
                        </p>
                        <p>
                          <strong>Subtotal:</strong> ${((orderData.subtotal_in_cents || 0) / 100).toFixed(2)}
                        </p>
                        <p>
                          <strong>Subtotal Discount:</strong> -$
                          {((orderData.items_discount_in_cents || 0) / 100).toFixed(2)}
                        </p>

                        <p>
                          <strong>Gift Card Credit Deduction:</strong> -$
                          {(() => {
                            const giftPayment = orderData.payments?.find(payment => payment.type === 'GiftCardPayment')
                            return giftPayment && giftPayment.total_cents
                              ? (giftPayment.total_cents / 100).toFixed(2)
                              : '0.00'
                          })()}
                        </p>
                        <p>
                          <strong>Points Credit Deduction:</strong> -$
                          {(() => {
                            const pointsPayment = orderData.payments?.find(
                              payment => payment.type === 'LmsPointsPayment'
                            )
                            return pointsPayment && pointsPayment.total_cents
                              ? (pointsPayment.total_cents / 100).toFixed(2)
                              : '0.00'
                          })()}
                        </p>
                        <p>
                          <strong>Account Credit Deduction:</strong> -$
                          {(() => {
                            const creditPayment = orderData.payments?.find(
                              payment => payment.type === 'CreditNotePayment'
                            )
                            return creditPayment && creditPayment.total_cents
                              ? (creditPayment.total_cents / 100).toFixed(2)
                              : '0.00'
                          })()}
                        </p>
                        <p>
                          <strong>Shipping:</strong> ${((orderData.shipment_in_cents || 0) / 100).toFixed(2)}
                        </p>
                        <p>
                          <strong>Shipping Discount:</strong> -$
                          {((orderData.shipping_discount_in_cents || 0) / 100).toFixed(2)}
                        </p>
                        <p>
                          <strong>Sales Tax:</strong> ${((orderData.total_tax_cents || 0) / 100).toFixed(2)}
                        </p>
                        {orderData.custom_checkout_fee > 0 && (
                          <p>
                            <strong>{orderData.custom_checkout_fee_alias || 'Checkout Fee'}:</strong> $
                            {((orderData.custom_checkout_fee || 0) / 100).toFixed(2)}
                          </p>
                        )}
                        <p>
                          <strong>Grand Total:</strong> ${((orderData.total_cents || 0) / 100).toFixed(2)}
                        </p>
                      </CardBody>
                    </Card>
                    <Card className="mb-3">
                      <CardHeader style={{ color: 'var(--mf-white)' }}>
                        <strong>Payment Method</strong>
                      </CardHeader>
                      <CardBody>
                        {orderData.payments?.[0]?.type && (
                          <p>
                            <strong>Type:</strong> {orderData.payments[0].type.replace(/([A-Z])/g, ' $1').trim()}
                          </p>
                        )}
                        {orderData.payments?.[0]?.card_last_four_digits && (
                          <p>
                            <strong>Last 4 Digits:</strong> {orderData.payments[0].card_last_four_digits}
                          </p>
                        )}
                        {orderData.invoice_number && (
                          <p>
                            <strong>Invoice #:</strong> {orderData.invoice_number}
                          </p>
                        )}
                        {orderData.po_number && (
                          <p>
                            <strong>PO Number:</strong> {orderData.po_number}
                          </p>
                        )}
                      </CardBody>
                    </Card>
                    <Card className="mb-3">
                      <CardHeader style={{ color: 'var(--mf-white)' }}>
                        <strong>Promotions</strong>
                      </CardHeader>
                      <CardBody>
                        {orderData.promotion_orders?.length > 0 ? (
                          orderData.promotion_orders.map((promo, idx) => (
                            <p key={idx}>
                              {promo.name} {promo.promotion_type === 'coupon' ? `(${promo.code_used})` : ''}
                            </p>
                          ))
                        ) : (
                          <p>No promotions applied.</p>
                        )}
                      </CardBody>
                    </Card>
                    <Card className="mb-3">
                      <CardHeader style={{ color: 'var(--mf-white)' }}>
                        <strong>Setup Charges</strong>
                      </CardHeader>
                      <CardBody>
                        {orderData.order_items?.some(item => item.setup_charge > 0) ? (
                          <ul>
                            {orderData.order_items
                              .filter(item => item.setup_charge > 0)
                              .map((item, idx) => (
                                <li key={idx}>
                                  {item.product_name}: ${((item.setup_charge || 0) / 100).toFixed(2)}
                                </li>
                              ))}
                          </ul>
                        ) : (
                          <p>No setup charges.</p>
                        )}
                      </CardBody>
                    </Card>

                    <Card className="mb-3">
                      <CardHeader style={{ color: 'var(--mf-white)' }}>
                        <strong>Shipping & Billing</strong>
                      </CardHeader>
                      <CardBody>
                        <p>
                          <strong>Shipping:</strong>
                        </p>
                        {orderData.customer_address?.company_name && (
                          <p>
                            <strong>Company:</strong> {orderData.customer_address.company_name}
                          </p>
                        )}
                        <p>{orderData.customer_address?.full_name || orderData.customer_address?.name}</p>
                        <p>{orderData.customer_address?.address_1}</p>
                        {orderData.customer_address?.address_2 && <p>{orderData.customer_address.address_2}</p>}
                        <p>
                          {orderData.customer_address?.city}, {orderData.customer_address?.state}{' '}
                          {orderData.customer_address?.zip}
                        </p>
                        <p>{orderData.customer_address?.country}</p>
                        {orderData.customer_address?.phone_number && (
                          <p>
                            <strong>Phone:</strong> {orderData.customer_address.phone_number}
                          </p>
                        )}
                        <hr />
                        <p>
                          <strong>Billing:</strong>
                        </p>
                        {orderData.billing_address ? (
                          <>
                            {orderData.billing_address?.company_name && (
                              <p>
                                <strong>Company:</strong> {orderData.billing_address.company_name}
                              </p>
                            )}
                            <p>{orderData.billing_address?.full_name || orderData.billing_address?.name}</p>
                            <p>{orderData.billing_address?.address_1}</p>
                            {orderData.billing_address?.address_2 && <p>{orderData.billing_address.address_2}</p>}
                            <p>
                              {orderData.billing_address?.city}, {orderData.billing_address?.state}{' '}
                              {orderData.billing_address?.zip}
                            </p>
                            <p>{orderData.billing_address?.country}</p>
                            {orderData.billing_address?.phone_number && (
                              <p>
                                <strong>Phone:</strong> {orderData.billing_address.phone_number}
                              </p>
                            )}
                          </>
                        ) : (
                          <p>No billing address on file.</p>
                        )}
                      </CardBody>
                    </Card>
                  </div>
                </div>
                <div className="row mt-4">
                  <div className="col-12">
                    <Card>
                      <CardHeader style={{ color: 'var(--mf-white)' }}>
                        <strong>Sub Orders</strong>
                      </CardHeader>
                      <CardBody>
                        {orderData.sub_orders?.length > 0 ? (
                          orderData.sub_orders.map((sub, idx) => (
                            <div key={idx} className="mb-4 card">
                              <div
                                className="d-flex justify-content-between align-items-center mb-2 card-header"
                                style={{ color: 'var(--mf-white)' }}
                              >
                                <div>
                                  <span className="fw-bold fs-5">
                                    {orderData.order_number}-{sub.sub_id}
                                  </span>
                                  <span className="ms-2">({sub.id})</span>
                                </div>
                                <div>
                                  <span className="badge bg-light text-dark">Customer Address</span>
                                </div>
                              </div>
                              <div className="p-3">
                                <div className="row">
                                  <div className="col-md-7">
                                    <div>
                                      <b>Status:</b> {sub.sub_order_status || 'N/A'}
                                    </div>
                                    <div>
                                      <b>Shipping Date:</b>{' '}
                                      {sub.shipping_date ? moment(sub.shipping_date).format('MMMM Do YYYY') : 'None'}
                                    </div>
                                    <div>
                                      <b>Fulfilment type:</b> {sub.fulfilment_type || sub.fulfillment_type || 'N/A'}
                                    </div>
                                    <div>
                                      <b>Status Description:</b>{' '}
                                      {sub.status_description || 'Order is awaiting processing'}
                                    </div>
                                    <div>
                                      <b>Tracking Link:</b>{' '}
                                      {sub.tracking_number ? (
                                        <a href={sub.tracking_number} target="_blank" rel="noopener noreferrer">
                                          {sub.tracking_number}
                                        </a>
                                      ) : (
                                        'N/A'
                                      )}
                                    </div>
                                    <div>
                                      <b>Insurance Cost:</b> N/A
                                    </div>
                                    <div>
                                      <b>Customer Notes:</b> {sub.external_notes || 'N/A'}
                                    </div>
                                    <div>
                                      <b>Tags:</b>{' '}
                                      {Array.isArray(sub.sub_order_tags) ? sub.sub_order_tags.join(', ') : ''}
                                    </div>
                                  </div>
                                  <div className="col-md-5">
                                    <div>
                                      <b>Name:</b> {sub.address?.full_name || ''}
                                    </div>
                                    {sub.address?.company_name && (
                                      <div>
                                        <b>Company:</b> {sub.address.company_name}
                                      </div>
                                    )}
                                    <div>
                                      <b>Address 1:</b> {sub.address?.address_line_1 || ''}
                                    </div>
                                    <div>
                                      <b>City:</b> {sub.address?.city || ''}
                                    </div>
                                    <div>
                                      <b>State:</b> {sub.address?.region || ''}
                                    </div>
                                    <div>
                                      <b>Zip:</b> {sub.address?.zip_code || ''}
                                    </div>
                                    <div>
                                      <b>Country:</b> {sub.address?.country || ''}
                                    </div>
                                    <div>
                                      <b>Phone Number:</b> {sub.address?.phone_number || ''}
                                    </div>
                                    <div>
                                      <b>Validation:</b>{' '}
                                      {sub.address?.address_residential_indicator ? (
                                        <span>
                                          <i className="fa fa-question-circle" />{' '}
                                          {sub.address.address_residential_indicator.charAt(0).toUpperCase() +
                                            sub.address.address_residential_indicator.slice(1)}
                                        </span>
                                      ) : (
                                        <span>
                                          <i className="fa fa-question-circle" /> Unknown
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="px-3 pb-3">
                                <table className="table table-bordered">
                                  <thead>
                                    <tr>
                                      <th>PRODUCT SKU..</th>
                                      <th>PRODUCT NAME</th>
                                      <th>QUANTITY</th>
                                      <th>PRICE</th>
                                      <th>SUBTOTAL</th>
                                      {subOrderHasDigitalProofUrls(sub) && <th>PROOF</th>}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {sub.sub_order_items && sub.sub_order_items.length > 0 ? (
                                      sub.sub_order_items.map((item, i) => {
                                        const digitalProofUrl = digitalProofUrlFor(item)

                                        return (
                                          <tr key={i}>
                                            <td>{item.product_sku || ''}</td>
                                            <td>{item.product_name || ''}</td>
                                            <td>{item.quantity || ''}</td>
                                            <td>${item.price_cents ? (item.price_cents / 100).toFixed(2) : ''}</td>
                                            <td>
                                              $
                                              {item.price_cents && item.quantity
                                                ? ((item.price_cents * item.quantity) / 100).toFixed(2)
                                                : ''}
                                            </td>
                                            {subOrderHasDigitalProofUrls(sub) && (
                                              <td>
                                                {digitalProofUrl ? (
                                                  <a
                                                    href={digitalProofUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={digitalProofLabelFor(item)}
                                                  >
                                                    View Proof
                                                  </a>
                                                ) : null}
                                              </td>
                                            )}
                                          </tr>
                                        )
                                      })
                                    ) : (
                                      <tr>
                                        <td colSpan={subOrderHasDigitalProofUrls(sub) ? 6 : 5}>No products</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p>No sub-orders available.</p>
                        )}
                      </CardBody>
                    </Card>
                  </div>
                </div>
                {canReviewOrder && (
                  <div className="row mt-4">
                    <div className="col-12">
                      <Card>
                        <CardHeader style={{ color: 'var(--mf-white)' }}>
                          <strong>Order Approval</strong>
                        </CardHeader>
                        <CardBody>
                          <FormGroup>
                            <Label htmlFor="approvalNotes">Approval Notes</Label>
                            <Input
                              type="textarea"
                              id="approvalNotes"
                              value={notes}
                              onChange={e => setNotes(e.target.value)}
                              placeholder="Add notes here..."
                              rows={4}
                            />
                          </FormGroup>
                          <div className="d-flex justify-content-end gap-2 mt-3">
                            <Button
                              color="danger"
                              disabled={denyDisabled}
                              onClick={() => submitOrderApproval(orderData, notes, false)}
                            >
                              {submitting ? 'Submitting...' : 'Deny Order'}
                            </Button>
                            <Button
                              color="success"
                              disabled={approveDisabled}
                              onClick={() => submitOrderApproval(orderData, notes, true)}
                            >
                              {submitting ? 'Submitting...' : 'Approve Order'}
                            </Button>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <FormGroup>
                <Label htmlFor="approvalNotes">Approval Notes</Label>
                <Input
                  type="textarea"
                  id="approvalNotes"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Add notes here..."
                />
              </FormGroup>
            )}
          </>
        )}
      </ModalBody>
      {(modalType === 'approve' || modalType === 'deny') && (
        <ModalFooter>
          <Button
            color={modalType === 'approve' ? 'success' : 'danger'}
            disabled={modalType === 'approve' ? approveDisabled : denyDisabled}
            onClick={() => submitOrderApproval(orderData, notes, modalType === 'approve')}
          >
            {submitting ? 'Submitting...' : modalType === 'approve' ? 'Approve Order' : 'Deny Order'}
          </Button>
        </ModalFooter>
      )}
    </Modal>
  )
}

OrderDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  size: PropTypes.string,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  modalType: PropTypes.string,
  onApprove: PropTypes.func,
  onDeny: PropTypes.func,
  initialOrder: PropTypes.object,
  currentUser: PropTypes.object
}

export default OrderDetailsModal
